import { useState, useRef } from 'react'
import { Upload, Loader2, RotateCcw, FileUp } from 'lucide-react'
import { analyzeLogs } from '../utils/analysis'
import type { AnalysisResult } from '../types'

interface LogInputProps {
  onAnalysisStart: () => void
  onAnalysisComplete: (result: AnalysisResult) => void
  onAnalysisError: () => void
  isAnalyzing: boolean
  onReset: () => void
  hasResults: boolean
}

const SAMPLE_LOG = `{
  "awsRegion": "us-east-1",
  "errorCode": "Client.VPCIdNotSpecified",
  "errorMessage": "No default VPC for this user",
  "eventCategory": "Management",
  "eventID": "12345678-9012-3456-7890-123456789012",
  "eventName": "AuthorizeSecurityGroupIngress",
  "eventSource": "ec2.amazonaws.com",
  "eventTime": "2025-03-03T15:26:45Z",
  "eventType": "AwsApiCall",
  "eventVersion": "1.10",
  "managementEvent": true,
  "readOnly": false,
  "recipientAccountId": "123456789012",
  "requestID": "12345678-9012-3456-7890-123456789012",
  "requestParameters": {
    "cidrIp": "0.0.0.0/0",
    "fromPort": 1,
    "groupName": "launch-wizard-5",
    "ipPermissions": {},
    "ipProtocol": "tcp",
    "toPort": 65535
  },
  "responseElements": null,
  "sourceIPAddress": "207.244.249.244",
  "tlsDetails": {
    "cipherSuite": "TLS_AES_128_GCM_SHA256",
    "clientProvidedHostHeader": "ec2.us-east-1.amazonaws.com",
    "tlsVersion": "TLSv1.3"
  },
  "userAgent": "Boto3/1.36.21 md/Botocore#1.36.21 ua/2.0 os/macos#24.1.0 md/arch#arm64 lang/python#3.13.2 md/pyimpl#CPython exec-env/grimoire_ae28dd36-0cd0-424d-a682-7f32f46d72c3 cfg/retry-mode#adaptive Botocore/1.36.21",
  "userIdentity": {
    "accessKeyId": "AKIA123456789EXAMPLE",
    "accountId": "123456789012",
    "arn": "arn:aws:iam:us-east-1:123456789012:placeholder-resource-123456789",
    "principalId": "AKIA123456789EXAMPLE",
    "sessionContext": {
      "attributes": {
        "creationDate": "2025-03-03T15:26:45Z",
        "mfaAuthenticated": "false"
      },
      "sessionIssuer": {
        "accountId": "123456789012",
        "arn": "arn:aws:iam:us-east-1:123456789012:placeholder-resource-123456789",
        "principalId": "AKIA123456789EXAMPLE",
        "type": "Role",
        "userName": "user-12345678"
      }
    },
    "type": "AssumedRole"
  }
}`

export default function LogInput({ 
  onAnalysisStart, 
  onAnalysisComplete, 
  onAnalysisError,
  isAnalyzing, 
  onReset, 
  hasResults 
}: LogInputProps) {
  const [logs, setLogs] = useState('')
  const [error, setError] = useState('')
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleAnalyze = async () => {
    if (!logs.trim()) {
      setError('Please enter some logs to analyze')
      return
    }

    setError('')
    onAnalysisStart()

    try {
      const result = await analyzeLogs(logs)
      onAnalysisComplete(result)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to analyze logs. Please try again.'
      setError(errorMessage)
      console.error('Analysis error:', err)
      onAnalysisError()
    }
  }

  const handleSampleLoad = () => {
    setLogs(SAMPLE_LOG)
    setError('')
  }

  const handleClear = () => {
    setLogs('')
    setError('')
    onReset()
  }

  const handleFileImport = () => {
    fileInputRef.current?.click()
  }

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    // Check file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('File size must be less than 10MB')
      return
    }

    // Check file type
    const allowedTypes = ['.json', '.log', '.txt', '.yaml', '.yml']
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase()
    
    if (!allowedTypes.includes(fileExtension)) {
      setError('Please select a valid log file (.json, .log, .txt, .yaml, .yml)')
      return
    }

    try {
      const content = await file.text()
      setLogs(content)
      setError('')
    } catch (err) {
      setError('Failed to read file. Please try again.')
      console.error('File reading error:', err)
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  return (
    <div className="rounded-lg">
      <div className="flex flex-col sm:flex-row sm:items-center justify-start mb-4 space-y-2 sm:space-y-0">
        <div className="flex flex-wrap gap-2">
          <button
            onClick={handleFileImport}
            className="btn-secondary text-sm flex items-center space-x-1"
            disabled={isAnalyzing}
          >
            <FileUp className="h-4 w-4" />
            <span>Import File</span>
          </button>
          <button
            onClick={handleSampleLoad}
            className="btn-secondary text-sm"
            disabled={isAnalyzing}
          >
            Load Sample
          </button>
          {(logs || hasResults) && (
            <button
              onClick={handleClear}
              className="btn-secondary text-sm flex items-center space-x-1"
              disabled={isAnalyzing}
            >
              <RotateCcw className="h-4 w-4" />
              <span>Clear</span>
            </button>
          )}
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <label htmlFor="logs" className="sr-only">
            Cloud telemetry logs input for security analysis
          </label>
          <textarea
            id="logs"
            name="cloudLogs"
            value={logs}
            onChange={(e) => setLogs(e.target.value)}
            placeholder="Paste your cloud telemetry logs here...&#10;&#10;Supported formats:&#10;• AWS CloudTrail&#10;• GCP Audit Logs&#10;• JSON format preferred"
            className="w-full h-64 px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none font-mono text-sm placeholder-gray-500 dark:placeholder-gray-400"
            disabled={isAnalyzing}
            aria-describedby={error ? "logs-error" : "logs-help"}
            aria-invalid={error ? "true" : "false"}
            spellCheck="false"
            autoComplete="off"
            aria-label="Cloud telemetry logs input for security threat analysis"
          />
          <div id="logs-help" className="sr-only">
            Enter your cloud logs in JSON format for security analysis. Supports AWS CloudTrail and GCP Audit logs.
          </div>
        </div>

        {error && (
          <div 
            id="logs-error" 
            className="text-red-600 dark:text-red-400 text-sm"
            role="alert"
            aria-live="polite"
          >
            {error}
          </div>
        )}

        <div className="flex flex-col sm:flex-row sm:items-center justify-between space-y-4 sm:space-y-0">
          <button
            onClick={handleAnalyze}
            disabled={isAnalyzing || !logs.trim()}
            className="btn-primary flex items-center justify-center space-x-2 w-full sm:w-auto"
            aria-describedby="analyze-description"
            type="submit"
          >
            {isAnalyzing ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                <span>Analyzing...</span>
              </>
            ) : (
              <>
                <Upload className="h-4 w-4" aria-hidden="true" />
                <span>Analyze Logs</span>
              </>
            )}
          </button>
          <div id="analyze-description" className="sr-only">
            Click to analyze your cloud logs for security threats and vulnerabilities using MITRE ATT&CK framework
          </div>
        </div>
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".json,.log,.txt,.yaml,.yml"
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />
    </div>
  )
}